import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text, Toggle } from '@spark';
import CopyFieldButton from './CopyFieldButton';
import FieldLabelWithHelp from './FieldLabelWithHelp';
import InferFromResponseAction from './InferFromResponseAction';
import JsonCodeTextArea from './JsonCodeTextArea';
import { getJsonSchemaError } from '../utils/inferJsonSchema';

const defaultHelpText = `Optional JSON schema for the model's output. Turn the toggle on to use structured output; leave it off to skip.
The root must be {"type": "object", "properties": {...}} — OpenAI structured output rejects string/array roots.
Use "Infer from response" to prefill from a JSON-object teacher sample. When set, teacher responses are constrained to this schema, and it is passed to the LLM judge as the expected output format.`;

const hasSchemaText = (schemaText) => Boolean((schemaText || '').trim());

const emitSchemaValue = (onChange, nextValue) => {
    onChange?.({ target: { value: nextValue } });
};

/**
 * Optional JSON schema editor. Off by default: the textarea and
 * "Infer from response" stay hidden until the operator turns the toggle on
 * (or a saved schema is loaded). Turning the toggle off clears the schema
 * so a single infer click cannot leave structured output enabled.
 */
const JsonSchemaInput = ({
    value,
    onChange,
    role,
    labelText = 'JSON schema',
    helpText = defaultHelpText,
    onInferFromResponse = null,
    inferDisabled = false,
    loading = false,
    rows = 5,
    showLabel = true,
    showInferAction = true,
    showEnableToggle = true,
    fieldDisabled = false,
    enabled: enabledProp,
    onEnabledChange,
}) => {
    const schemaError = useMemo(() => getJsonSchemaError(value), [value]);
    const isViewer = role === 'Viewer';
    const isDisabled = isViewer || loading || fieldDisabled;
    const isControlled = typeof enabledProp === 'boolean';
    const [internalEnabled, setInternalEnabled] = useState(() => hasSchemaText(value));
    const enabled = isControlled ? enabledProp : internalEnabled;
    const wasFieldDisabledRef = useRef(fieldDisabled);

    const setEnabledState = (next) => {
        if (!isControlled) {
            setInternalEnabled(next);
        }
        onEnabledChange?.(next);
    };

    useEffect(() => {
        if (!hasSchemaText(value)) {
            return;
        }
        if (!isControlled) {
            setInternalEnabled(true);
        } else if (!enabledProp) {
            onEnabledChange?.(true);
        }
    }, [value, isControlled, enabledProp, onEnabledChange]);

    useEffect(() => {
        if (wasFieldDisabledRef.current && !fieldDisabled && showEnableToggle) {
            setEnabledState(true);
        }
        wasFieldDisabledRef.current = fieldDisabled;
        // setEnabledState is stable enough for this mount/unmount-style flip.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldDisabled, showEnableToggle]);

    const schemaOn = !showEnableToggle || enabled;
    const showInfer = schemaOn && showInferAction;
    const showHeaderRow = showLabel || showEnableToggle || (
        showInfer && typeof onInferFromResponse === 'function'
    );

    const handleEnableChange = (event) => {
        const checked = event.target.checked;
        setEnabledState(checked);
        if (!checked) {
            emitSchemaValue(onChange, '');
        }
    };

    return (
        <div className="model-setup-json-schema">
            {showHeaderRow ? (
                <div className="model-setup-json-schema__header">
                    {showLabel || showInfer ? (
                        <div className="model-setup-json-schema__label-row">
                            {showLabel ? (
                                <FieldLabelWithHelp
                                    label={labelText}
                                    helpText={helpText}
                                    style={{ marginBottom: 0 }}
                                />
                            ) : null}
                            {showInfer ? (
                                <InferFromResponseAction
                                    onInferFromResponse={onInferFromResponse}
                                    inferDisabled={inferDisabled}
                                    loading={loading}
                                />
                            ) : null}
                        </div>
                    ) : (
                        <span />
                    )}
                    {showEnableToggle ? (
                        <div className="model-setup-json-schema__actions">
                            <div className="model-setup-json-schema__toggle">
                                <Text variant="base-regular" color="secondary">
                                    {schemaOn ? 'Enabled' : 'Not used'}
                                </Text>
                                <Toggle
                                    size="small"
                                    active={schemaOn}
                                    disabled={isDisabled}
                                    aria-label="Use JSON schema"
                                    onChange={handleEnableChange}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {schemaOn ? (
                <>
                    <div className="model-setup-json-schema__editor">
                        <CopyFieldButton value={value} />
                        <JsonCodeTextArea
                            placeholder={'e.g. {"type": "object", "properties": {"summary": {"type": "string"}}}'}
                            value={value || ''}
                            onChange={onChange}
                            rows={rows}
                            disabled={isDisabled}
                        />
                    </div>
                    {schemaError ? (
                        <div className="model-setup-json-schema__error fs-unmask">
                            <Text variant="detail-regular" as="div">
                                {schemaError}
                            </Text>
                        </div>
                    ) : null}
                </>
            ) : null}
        </div>
    );
};

export default JsonSchemaInput;
